import { ProjectFormElement } from "../types";
import { normalizeCurrencyCode } from "./currency";

type ProjectFinanceInputs = {
  projectAmount: number | null;
  projectCurrency: string;
  invoiceEnabled: boolean;
  vatRate: number;
  corporateTaxRate: number;
  realizedCost: number;
  totalEstimatedCost: number | null;
  totalCollected?: number | null;
};

export type ProjectFinanceResolvedInputs = {
  projectAmount: number | null;
  projectCurrency: string;
  invoiceEnabled: boolean;
};

export type ProjectFinanceSummary = ProjectFinanceResolvedInputs & {
  vatRate: number;
  corporateTaxRate: number;
  invoiceTotal: number | null;
  totalCollected: number | null;
  remainingCollection: number | null;
  isCurrencySupported: boolean;
  grossProfit: number | null;
  netProfit: number | null;
  estimatedGrossProfit: number | null;
  estimatedNetProfit: number | null;
};

const OFFER_AMOUNT_KEYS = ["offer_amount", "offerAmount"];
const INVOICE_KEYS = ["invoice", "fatura"];
const CURRENCY_KEYS = [
  "projectCurrency",
  "currency",
  "currencyCode",
  "para_birimi",
];
const ELEMENT_CURRENCY_KEYS = ["elementCurrencies", "element_currencies"];

const YES_VALUES = new Set(["1", "true", "yes", "evet", "var", "on"]);
const NO_VALUES = new Set(["0", "false", "no", "hayir", "hayır", "yok", "off"]);

const normalizeText = (value: string): string =>
  value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ");

export const parseProjectNumericValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const sanitized = trimmed.replace(/\s+/g, "").replace(/[^\d,.-]/g, "");
  if (!sanitized) {
    return null;
  }

  const commaCount = (sanitized.match(/,/g) || []).length;
  const dotCount = (sanitized.match(/\./g) || []).length;
  const lastComma = sanitized.lastIndexOf(",");
  const lastDot = sanitized.lastIndexOf(".");

  let normalized = sanitized;

  if (lastComma !== -1 && lastDot !== -1) {
    normalized =
      lastComma > lastDot
        ? sanitized.replace(/\./g, "").replace(",", ".")
        : sanitized.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized =
      commaCount > 1
        ? sanitized.replace(/,/g, "")
        : /,\d{1,2}$/.test(sanitized)
          ? sanitized.replace(",", ".")
          : sanitized.replace(/,/g, "");
  } else if (lastDot !== -1) {
    normalized =
      dotCount > 1
        ? sanitized.replace(/\./g, "")
        : /\.\d{1,2}$/.test(sanitized)
          ? sanitized
          : sanitized.replace(/\./g, "");
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseBooleanValue = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  if (typeof value !== "string") {
    return false;
  }

  const normalized = normalizeText(value);
  if (YES_VALUES.has(normalized)) {
    return true;
  }
  if (NO_VALUES.has(normalized)) {
    return false;
  }
  return false;
};

const getValueFromKeys = (
  formData: Record<string, any>,
  keys: string[],
): unknown => {
  for (const key of keys) {
    if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
      return formData[key];
    }
  }
  return undefined;
};

const extractElementCurrencies = (
  formData: Record<string, any>,
): Record<string, string> => {
  const raw = getValueFromKeys(formData, ELEMENT_CURRENCY_KEYS);
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce<
    Record<string, string>
  >((acc, [elementId, currencyCode]) => {
    if (!elementId) {
      return acc;
    }
    acc[elementId] = normalizeCurrencyCode(currencyCode);
    return acc;
  }, {});
};

const findElementByType = (
  formElements: ProjectFormElement[] | null | undefined,
  type: ProjectFormElement["type"],
): ProjectFormElement | null =>
  formElements?.find((element) => element.type === type) || null;

export const resolveProjectFinanceInputs = (
  formData: Record<string, any> | null | undefined,
  formElements?: ProjectFormElement[] | null,
): ProjectFinanceResolvedInputs => {
  const safeFormData = formData || {};

  const offerAmountElement = findElementByType(formElements, "offer_amount");
  const invoiceElement = findElementByType(formElements, "invoice");
  const elementCurrencies = extractElementCurrencies(safeFormData);

  const rawProjectAmount =
    (offerAmountElement ? safeFormData[offerAmountElement.id] : undefined) ??
    getValueFromKeys(safeFormData, OFFER_AMOUNT_KEYS);

  const rawInvoiceValue =
    (invoiceElement ? safeFormData[invoiceElement.id] : undefined) ??
    getValueFromKeys(safeFormData, INVOICE_KEYS);

  const offerAmountCurrency =
    (offerAmountElement ? elementCurrencies[offerAmountElement.id] : undefined) ??
    elementCurrencies.offer_amount ??
    elementCurrencies.offerAmount;

  const rawCurrencyValue =
    offerAmountCurrency ??
    getValueFromKeys(safeFormData, CURRENCY_KEYS) ??
    offerAmountElement?.currency;

  return {
    projectAmount: parseProjectNumericValue(rawProjectAmount),
    projectCurrency: normalizeCurrencyCode(rawCurrencyValue),
    invoiceEnabled: parseBooleanValue(rawInvoiceValue),
  };
};

export const getProjectTaxSettings = (
  companySettings: Record<string, any> | null | undefined,
): { vatRate: number; corporateTaxRate: number } => {
  const vatRate = Number(companySettings?.taxes?.vatRate);
  const corporateTaxRate = Number(companySettings?.taxes?.corporateTaxRate);

  return {
    vatRate: Number.isFinite(vatRate) ? vatRate : 20,
    corporateTaxRate: Number.isFinite(corporateTaxRate)
      ? corporateTaxRate
      : 25,
  };
};

export const calculateProjectFinanceSummary = ({
  projectAmount,
  projectCurrency,
  invoiceEnabled,
  vatRate,
  corporateTaxRate,
  realizedCost,
  totalEstimatedCost,
  totalCollected = null,
}: ProjectFinanceInputs): ProjectFinanceSummary => {
  const isCurrencySupported =
    Number.isFinite(realizedCost) &&
    (totalEstimatedCost === null || Number.isFinite(totalEstimatedCost));
  const safeRealizedCost = Number.isFinite(realizedCost) ? realizedCost : 0;
  const safeEstimatedCost =
    totalEstimatedCost !== null && Number.isFinite(totalEstimatedCost)
      ? totalEstimatedCost
      : null;

  const invoiceTotal =
    projectAmount !== null
      ? invoiceEnabled
        ? projectAmount * (1 + vatRate / 100)
        : projectAmount
      : null;
  const safeTotalCollected =
    totalCollected !== null && totalCollected !== undefined && Number.isFinite(totalCollected)
      ? totalCollected
      : null;
  const remainingCollection =
    invoiceTotal !== null && safeTotalCollected !== null
      ? invoiceTotal - safeTotalCollected
      : invoiceTotal;

  if (projectAmount === null || !isCurrencySupported) {
    return {
      projectAmount,
      projectCurrency,
      invoiceEnabled,
      vatRate,
      corporateTaxRate,
      invoiceTotal,
      totalCollected: safeTotalCollected,
      remainingCollection,
      isCurrencySupported,
      grossProfit: null,
      netProfit: null,
      estimatedGrossProfit: null,
      estimatedNetProfit: null,
    };
  }

  const grossProfit = projectAmount - safeRealizedCost;
  const netProfit = invoiceEnabled
    ? grossProfit * (1 - corporateTaxRate / 100)
    : grossProfit;
  const estimatedGrossProfit =
    safeEstimatedCost !== null ? projectAmount - safeEstimatedCost : null;
  const estimatedNetProfit =
    estimatedGrossProfit !== null
      ? invoiceEnabled
        ? estimatedGrossProfit * (1 - corporateTaxRate / 100)
        : estimatedGrossProfit
      : null;

  return {
    projectAmount,
    projectCurrency,
    invoiceEnabled,
    vatRate,
    corporateTaxRate,
    invoiceTotal,
    totalCollected: safeTotalCollected,
    remainingCollection,
    isCurrencySupported,
    grossProfit,
    netProfit,
    estimatedGrossProfit,
    estimatedNetProfit,
  };
};
